import { useState } from 'react'
import { app, translate } from "../app"
import { translations } from "../translations"
import { Link } from 'react-router-dom'
import { languages } from '../locale'
import { isExternalLink, formatMenuTitle, menuContainsExternalLinks } from '../helpers'
import { DownOutlined, LeftOutlined, RightOutlined, UpOutlined } from './icons'

function MenuNavLink({
  link,
  locale,
  className,
  ariaLabel,
  onClick,
  children,
}: {
  link: string
  locale: string
  className?: string
  ariaLabel?: string
  onClick?: () => void
  children: React.ReactNode
}) {
  if (isExternalLink(link)) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {children}
      </a>
    )
  }

  return (
    <Link
      className={className}
      to={`/${locale.toLowerCase()}${link}`}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </Link>
  )
}

const DropdownContent = ({
  submenuscontent,
  setIsDrawerOpen,
  handleClick,
  uppercaseTitles: uppercaseTitlesProp,
}) => {
  const [activeItem, setActiveItem] = useState(null)
  const locale = languages[app.locale]?.zendesk as string ?? app.locale
  const isRTL = languages[app.locale]?.rtl
  const uppercaseTitles =
    uppercaseTitlesProp ?? menuContainsExternalLinks(submenuscontent)

  const handleSectionClick = (item: any) => {
    setActiveItem(activeItem === item ? null : item)
  }

  const handleSectionEnter = (item: any) => {
    setActiveItem(item)
  }

  return (
    <div>
      <div className="dropdown_content">
        {!setIsDrawerOpen && <span className='text-zinc-600 uppercase text-xs lg:text-sm mt-4 pb-6'>{translate(translations.resourcesCategories)}</span>}
        {!setIsDrawerOpen && <span className='text-zinc-600 text-xs lg:text-sm mt-4 pb-6 font-normal'>{translate(translations.topics)}</span>}
        {!setIsDrawerOpen && <span className='hidden xl:block !w-[1px] bg-[#ddd] mt-8 pb-6'></span>}
        {!setIsDrawerOpen && (
          <div className='hidden xl:flex justify-between mt-4 pb-6 invisible'>
            <span className='text-zinc-600 uppercase text-xs lg:text-sm'>{translate(translations.latestArticle)}</span>
            <Link to={`/${locale.toLowerCase()}/articles`} className='text-black'>
              <span className='text underline underline-offset-2 hover:opacity-80'>link</span>
            </Link>
          </div>
        )}
        <div className='category-section-container'>
          {submenuscontent?.map((item, index) => {
            const itemTitle = formatMenuTitle(translate(item.title), uppercaseTitles)
            return (
            <div key={`${itemTitle}-${index}`}>
              <section
                className={`parent-section flex justify-between cursor-pointer mt-0 text-base break-words whitespace-normal pl-4`}
                onMouseEnter={() => handleSectionEnter(item)}
                onClick={() => handleSectionClick(item)}
              >
                <MenuNavLink
                  link={item.link || ""}
                  locale={locale}
                  className={`lg:!ml-0 break-words whitespace-normal text-black ${setIsDrawerOpen ? 'my-2' : 'no-underline'} hover:text-gray-600 transition-colors`}
                  ariaLabel={itemTitle}
                  onClick={() => {
                    setIsDrawerOpen && setIsDrawerOpen(false)
                    handleClick()
                  }}
                >
                  {item.children && <h2 className='m-0 text-lg lg:text-xl break-words whitespace-normal font-bold'>{itemTitle}</h2>}
                  {!item.children && <span className='font-bold'>{itemTitle}</span>}
                </MenuNavLink>
                {item.children && <div className={`hidden lg:block ${isRTL ? 'mr-2' : 'ml-2'}`}>{isRTL ? <LeftOutlined aria-hidden="true" /> : <RightOutlined aria-hidden="true" />}</div>}
                {item.children && <button
                  className={`${isRTL ? 'ml-1' : 'mr-1'} lg:hidden nav-button`}
                  onClick={() => handleSectionClick(item)}
                  aria-label="Toggle dropdown"
                  aria-haspopup="menu"
                  aria-expanded={item === activeItem}
                >
                  {item === activeItem ? (
                    <UpOutlined aria-hidden="true" />
                  ) : (
                    <DownOutlined aria-hidden="true" />
                  )}
                </button>}
              </section>
              {item === activeItem && activeItem?.children?.map(({ title, link }, childIndex) => {
                const childTitle = formatMenuTitle(translate(title), uppercaseTitles)
                return (
                <section
                  className={'parent-section flex justify-between cursor-pointer mt-0 text-base break-words whitespace-normal pl-4 pr-4 lg:!hidden'}
                  key={`${childTitle}-${childIndex}`}
                  onClick={() => {
                    setIsDrawerOpen && setIsDrawerOpen(false)
                    handleClick()
                  }}>
                  <MenuNavLink
                    link={link || ""}
                    locale={locale}
                    className='no-underline text-black break-words whitespace-normal'
                    ariaLabel={childTitle}
                  >
                    {childTitle}
                  </MenuNavLink>
                </section>
              )})}
            </div>
          )})}
        </div>
        <div className='subcat-section-container !hidden lg:!flex'>
          {activeItem?.children?.map(({ title, link }, index) => {
            const childTitle = formatMenuTitle(translate(title), uppercaseTitles)
            return (
            <section
              key={index}
              onClick={() => {
                setIsDrawerOpen && setIsDrawerOpen(false)
                handleClick()
              }}>
              <MenuNavLink
                link={link || ""}
                locale={locale}
                className='!no-underline text-black break-words whitespace-normal'
                ariaLabel={childTitle}
              >
                {childTitle}
              </MenuNavLink>
            </section>
          )})}
        </div>
        <div className='hidden xl:block border-l border-l-[#DDDDDD] border-solid border-t-0 border-r-0 border-b-0'></div>
      </div>
    </div>
  )
}

export default DropdownContent
